import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import type {
    GetTileEnquiryContextQuery,
    GetTileEnquiryContextQueryVariables,
    EnquiryAudienceEnum,
} from 'generated/graphql';
import { GetTileEnquiryContextDocument } from 'generated/documents/GetTileEnquiryContextDocument';

export const TILE_ENQUIRY_CONTEXT_API_ROUTE = '/remix-api/tile-enquiry-context';

const QUERY_SCHEMA = z.object({
    link: z.string().min(1),
});

/** The professional enquiry context resolved lazily for a listing tile. */
export interface TileEnquiryProfessional {
    ID: number;
    Title: string;
    CompanyLogo: string;
    LogoBackgroundColor: string;
    CustomEnquiryMessage: string | null;
    EnquiryMessageOverrideEnabled: boolean;
    EnquiryMessagePlaceholder: string | null;
    EnquiryAudience: EnquiryAudienceEnum;
}

export interface TileEnquiryContextResponse {
    professional: TileEnquiryProfessional | null;
    error?: string;
}

/**
 * Resolves the professional enquiry context for a tile on demand (BC-4810).
 * Listing tiles don't carry the professional's enquiry fields, so the tile
 * Enquire button fetches them here (by the professional's Link) before opening
 * the standard enquiry modal. Branch options are intentionally omitted — the
 * tile enquiry uses the branch-less modal.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const parsed = QUERY_SCHEMA.safeParse(Object.fromEntries(url.searchParams));
    if (!parsed.success) {
        return routerData<TileEnquiryContextResponse>(
            { professional: null, error: 'Invalid query parameters' },
            { status: 400 },
        );
    }

    // Always answer with a body, never a thrown error: the caller keys "the
    // lookup finished" off `fetcher.data`, and a throw leaves that undefined —
    // holding the modal's header skeleton open forever.
    try {
        const result = await context.client.query<
            GetTileEnquiryContextQuery,
            GetTileEnquiryContextQueryVariables
        >({
            query: GetTileEnquiryContextDocument,
            variables: { Link: parsed.data.link },
        });

        const professional = result.data?.Directory?.Professional;
        if (!professional || professional.__typename !== 'Professional') {
            return routerData<TileEnquiryContextResponse>({
                professional: null,
            });
        }

        return routerData<TileEnquiryContextResponse>({
            professional: {
                ID: professional.ID,
                Title: professional.Title,
                CompanyLogo: professional.CompanyLogo ?? '',
                LogoBackgroundColor:
                    professional.LogoBackgroundColor ?? 'white',
                CustomEnquiryMessage: professional.CustomEnquiryMessage ?? null,
                EnquiryMessageOverrideEnabled:
                    professional.EnquiryMessageOverrideEnabled ?? false,
                EnquiryMessagePlaceholder:
                    professional.EnquiryMessagePlaceholder ?? null,
                EnquiryAudience: professional.EnquiryAudience,
            },
        });
    } catch (error) {
        context.logger.error(`Error in ${TILE_ENQUIRY_CONTEXT_API_ROUTE}`, {
            error,
        });
        return routerData<TileEnquiryContextResponse>(
            { professional: null, error: 'Unable to resolve enquiry context' },
            { status: 500 },
        );
    }
};
